import { CircleAlert, Info, TriangleAlert } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '../lib/api'
import { cn } from '../lib/cn'
import { errorMessage } from '../lib/errors'
import { Button } from './button'

/** Codes, document numbers and ids: monospace, left-to-right, never machine-translated. */
export function Code({ className, ...props }: ComponentProps<'code'>) {
  return (
    <code
      dir="ltr"
      translate="no"
      className={cn('font-mono text-[0.9375em] whitespace-nowrap text-ink tabular-nums', className)}
      {...props}
    />
  )
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('block h-3 rounded-xs bg-surface-sunken motion-safe:animate-pulse', className)}
    />
  )
}

export interface EmptyStateProps {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}

/** Invites the next action ("No customers yet. Add your first customer."). */
export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-1.5 px-4 py-12 text-center', className)}
    >
      {icon ? <div className="mb-1 text-ink-muted [&_svg]:size-6">{icon}</div> : null}
      <p className="text-base font-medium text-ink">{title}</p>
      {description ? <p className="max-w-sm text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  )
}

export interface ErrorStateProps {
  error: unknown
  title?: ReactNode
  onRetry?: (() => void) | undefined
  /** Replaces the retry button (e.g. a way out when retrying cannot help). */
  action?: ReactNode
  className?: string
}

/** Says what went wrong and what to do; shows the request reference for support. */
export function ErrorState({ error, title, onRetry, action, className }: ErrorStateProps) {
  const { t, i18n } = useTranslation()
  const requestId = isApiError(error) ? error.requestId : undefined
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center gap-1.5 px-4 py-12 text-center', className)}
    >
      <CircleAlert aria-hidden className="mb-1 size-6 text-ink-red" />
      <p className="text-base font-medium text-ink">{title ?? t('errors.title')}</p>
      <p className="max-w-md text-sm text-ink-muted">{errorMessage(t, i18n.language, error)}</p>
      {requestId ? (
        <p className="text-xs text-ink-muted">
          {t('errors.reference')} <Code className="text-xs text-ink-muted select-all">{requestId}</Code>
        </p>
      ) : null}
      {action ? (
        <div className="mt-3">{action}</div>
      ) : onRetry ? (
        <Button className="mt-3" onClick={onRetry}>
          {t('actions.retry')}
        </Button>
      ) : null}
    </div>
  )
}

export interface AlertProps {
  tone?: 'info' | 'warning' | 'danger'
  title?: ReactNode
  children?: ReactNode
  action?: ReactNode
  className?: string
  id?: string
}

const alertTones = {
  info: { box: 'border-stamp', icon: 'text-stamp', Icon: Info },
  warning: { box: 'border-ink-amber', icon: 'text-ink-amber', Icon: TriangleAlert },
  danger: { box: 'border-ink-red', icon: 'text-ink-red', Icon: CircleAlert },
} as const

/** An inline message inside a page or form (not a toast). */
export function Alert({ tone = 'info', title, children, action, className, id }: AlertProps) {
  const { box, icon, Icon } = alertTones[tone]
  return (
    <div
      id={id}
      role={tone === 'info' ? 'status' : 'alert'}
      data-slot="alert"
      data-tone={tone}
      tabIndex={-1}
      className={cn(
        'flex items-start gap-2.5 rounded-sm border border-s-4 bg-surface px-3 py-2.5',
        box,
        className,
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', icon)} />
      <div className="min-w-0 flex-1 text-sm">
        {title ? <p className="font-medium text-ink">{title}</p> : null}
        {children ? <div className="text-ink-muted">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
